import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';

function DeviceCommandManager({ devices, onCommandSent }) {
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [command, setCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Normalize devices to always be an array
  const devicesArray = devices || [];

  // Helper functions to get device types
  const getDeviceType = (capabilities) => {
    if (!capabilities) {
      return 'N/A';
    }
    if (typeof capabilities === 'object') {
      const model = capabilities.model;
      if (model) {
        const modelLower = model.toLowerCase();
        if (modelLower.includes('juganucap')) {
          return 'CAP';
        } else if (modelLower.includes('juganunode')) {
          return 'Node';
        } else if (modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb')) {
          return 'AI';
        } else if (modelLower.includes('edgecore') || modelLower.includes('eap102')) {
          return 'EdgeCore';
        }
      }
      // Also check compatible field for EdgeCore devices
      if (capabilities.compatible === 'edgecore_eap102') {
        return 'EdgeCore';
      }
    }
    return 'N/A';
  };



  const handleDeviceChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    
    // Check if CLEAR_ALL is selected
    if (selectedValues.includes('CLEAR_ALL')) {
      setSelectedDevices([]);
      return;
    }
    
    // Handle special bulk selection values
    let finalDevices = [];
    selectedValues.forEach(val => {
      if (val === 'ALL_DEVICES') {
        finalDevices = [...finalDevices, ...devicesArray.map(d => d.serial_number)];
      } else if (val === 'ALL_NODES') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'Node').map(d => d.serial_number)];
      } else if (val === 'ALL_CAPS') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'CAP').map(d => d.serial_number)];
      } else if (val === 'ALL_AI') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'AI').map(d => d.serial_number)];
      } else if (val === 'ALL_EDGECORE') {
        finalDevices = [...finalDevices, ...devicesArray.filter(d => getDeviceType(d.capabilities) === 'EdgeCore').map(d => d.serial_number)];
      } else {
        // Regular device serial number
        finalDevices.push(val);
      }
    });
    
    // Remove duplicates
    const uniqueDevices = [...new Set(finalDevices)];
    setSelectedDevices(uniqueDevices);
  };

  const handleSendCommand = async () => {
    if (!command.trim() || selectedDevices.length === 0) {
      enqueueSnackbar('Please enter a command and select at least one device', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.sendCommandBatch(selectedDevices, command.trim());
      const commandResults = api.mapBatchOperationResults(response).map((row) => ({
        deviceId: row.serial,
        success: row.success,
        error: row.success ? undefined : row.message,
      }));
      const successful = commandResults.filter(r => r.success);
      const failed = commandResults.filter(r => !r.success);

      const failedDeviceNames = failed.map(f => {
        const device = devicesArray.find(d => d.serial_number === f.deviceId);
        return device?.name || f.deviceId;
      });

      if (failed.length === 0) {
        enqueueSnackbar(`Command sent successfully to all ${selectedDevices.length} device(s)`, { variant: 'success' });
      } else if (successful.length === 0) {
        enqueueSnackbar(`Failed to send command to all ${selectedDevices.length} device(s)`, { variant: 'error' });
      } else {
        const failedList = failedDeviceNames.join(', ');
        enqueueSnackbar(
          `Command sent to ${successful.length} out of ${selectedDevices.length} device(s). Failed: ${failedList} (${failed.length} failed)`,
          { 
            variant: 'warning',
            autoHideDuration: 8000
          }
        );
      }
      onCommandSent();
    } catch (error) {
      console.error('Error sending command:', error);
      enqueueSnackbar('Failed to send command', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#f8f9fa',
        border: '1px solid #dee2e6',
      }}
    >
      <Typography variant="h5" gutterBottom color="primary">
        Device Command Manager
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {devicesArray.length === 0 ? (
        <Alert severity="info">No devices available. Please wait for devices to connect.</Alert>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Devices</InputLabel>
            <Select
              multiple
              value={selectedDevices}
              onChange={handleDeviceChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const device = devicesArray.find(d => d.serial_number === value);
                    return (
                      <Chip
                        key={value}
                        label={device?.name || value}
                        color={device?.connected ? 'success' : 'error'}
                        size="small"
                        onDelete={() => {
                          setSelectedDevices(prev => prev.filter(id => id !== value));
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {/* Bulk Selection Options */}
              <MenuItem value="ALL_DEVICES" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                📱 All Devices ({devicesArray.length})
              </MenuItem>
              <MenuItem value="ALL_NODES" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🔗 All Nodes ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'Node').length})
              </MenuItem>
              <MenuItem value="ALL_CAPS" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                📡 All CAPs ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'CAP').length})
              </MenuItem>
              <MenuItem value="ALL_AI" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🤖 All AI ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'AI').length})
              </MenuItem>
              <MenuItem value="ALL_EDGECORE" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🏢 All EdgeCore ({devicesArray.filter(d => getDeviceType(d.capabilities) === 'EdgeCore').length})
              </MenuItem>
              <MenuItem value="CLEAR_ALL" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                🗑️ Clear All
              </MenuItem>
              <Divider />
              {/* Individual Devices */}
              {devicesArray.map((device) => (
                <MenuItem key={device.serial_number} value={device.serial_number}>
                  {device.name || device.serial_number} ({getDeviceType(device.capabilities)} - {device.connected ? 'Connected' : 'Disconnected'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Command"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="Enter command to send to selected devices..."
            sx={{ bgcolor: 'white' }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSendCommand}
            disabled={loading || !command.trim() || selectedDevices.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            size="large"
            sx={{ mt: 'auto' }}
          >
            {loading ? 'Sending...' : 'Send Command'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default DeviceCommandManager; 